function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { CssContainer } from "../../components/container";
import { Loader } from "../../components/loaders";
import Auth from "../../functions/authenticate";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Pill } from "../../components/analytics-kit";
import { hasTenantFeature } from "../../functions/tenant-features";
import { EmailHeader } from "../email-automation/email-header";
import OutreachSettingsCard from "../outreach/settings-card";
import { api, errMsg, toast, idStr, num, railLabel, dateTime, isAdmin } from "./api";
import account from "../../data/account";
import { domainTone, domainLabel } from "./steps";

/*
 * Email → Settings (BULK_EMAIL_MANAGEMENT §6.2, §8; EMAIL redesign 2026-10-09, Settings mockup):
 * left column = "Who it's from" (sender identities, DNS verify in the edit dialog), "How it's sent" (the sending
 * services with connection state and the default) and a one-line Outreach mailboxes summary; right column =
 * "Sending rules" bound to GET/PUT settings, saved as you change (debounced PUT; Administrator-only on the API, so
 * the rules are read-only for everyone else).
 */

const RAIL_ORDER = ["plunk", "mailchimp", "constant_contact"]; // Smartlead retired 2026-10-03: outreach = Plunk Outreach
const RAIL_TEXT = {
  plunk: "Included. Tracks opens and clicks, adds an unsubscribe link to every email.",
  mailchimp: "Send through your Mailchimp account",
  constant_contact: "Send through your Constant Contact account"
};
const SAVE_DELAY = 800;

/** A small "···" menu: items [{ label, onClick, danger, disabled }]; closes on outside click / Escape. */
class RowMenu extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "outside", e => {
      if (this.state.open && this.ref.current && !this.ref.current.contains(e.target)) this.setState({
        open: false
      });
    });
    _defineProperty(this, "esc", e => {
      if (e.key === "Escape" && this.state.open) this.setState({
        open: false
      });
    });
    this.state = {
      open: false
    };
    this.ref = /*#__PURE__*/React.createRef();
  }
  componentDidMount() {
    document.addEventListener("mousedown", this.outside);
    document.addEventListener("keydown", this.esc);
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.outside);
    document.removeEventListener("keydown", this.esc);
  }
  render() {
    const items = (this.props.items || []).filter(Boolean);
    if (!items.length) return null;
    return /*#__PURE__*/React.createElement("span", {
      className: "ems-menu",
      ref: this.ref
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-more",
      "aria-label": this.props.label || "More",
      "aria-haspopup": "menu",
      "aria-expanded": this.state.open,
      onClick: () => this.setState({
        open: !this.state.open
      })
    }, "\xB7\xB7\xB7"), this.state.open ? /*#__PURE__*/React.createElement("div", {
      className: "ems-menu-list",
      role: "menu"
    }, items.map(it => /*#__PURE__*/React.createElement("button", {
      key: it.label,
      type: "button",
      role: "menuitem",
      className: it.danger ? "danger" : "",
      disabled: !!it.disabled,
      onClick: () => {
        this.setState({
          open: false
        });
        it.onClick();
      }
    }, it.label))) : null);
  }
}

/** Sender verification state in plain words: [text, tone]. */
function senderStatus(s) {
  const st = String(s.domainAuthStatus || "none").toLowerCase();
  if (st === "verified") return s.campaignDomain && s.campaignDomain.valid ? ["✓ Verified", "ok"] : ["Verified for journeys. Campaigns need one more DNS step", "warn"];
  if (st === "pending") return ["Waiting for DNS records", "warn"];
  if (st === "failed") return ["DNS check failed", "bad"];
  return ["Not verified yet", "dim"];
}

/** "1 spam report per 1,000 delivered" from a complaint-rate percent. */
function spamLimit(ratePct) {
  const r = Number(ratePct) || 0.1;
  const perK = r * 10;
  if (perK >= 1 && Math.abs(perK - Math.round(perK)) < 1e-6) return `${num(Math.round(perK))} spam report${Math.round(perK) === 1 ? "" : "s"} per 1,000 delivered`;
  return `1 spam report per ${num(Math.round(100 / r))} delivered`;
}
class EmailSendersViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "loadAll", () => {
      this.loadSenders();
      this.loadRails();
      this.loadSettings();
    });
    _defineProperty(this, "loadSenders", () => api("GET", "/senders").then(d => this._alive && this.setState({
      senders: (d && d.items || []).map(s => ({
        ...s,
        id: idStr(s.id)
      }))
    })).catch(e => this._alive && this.setState({
      senders: [],
      err: errMsg(e, "Senders could not be loaded.")
    })));
    _defineProperty(this, "loadRails", () => api("GET", "/rails").then(d => this._alive && this.setState({
      rails: d || {
        items: [],
        default: "plunk"
      }
    })).catch(e => this._alive && this.setState({
      rails: {
        items: [],
        default: "plunk"
      },
      err: errMsg(e, "Rails could not be loaded.")
    })));
    _defineProperty(this, "loadSettings", () => api("GET", "/settings").then(d => this._alive && this.setState({
      settings: d || {},
      settingsDraft: JSON.parse(JSON.stringify(d || {}))
    })).catch(() => this._alive && this.setState({
      settings: {},
      settingsDraft: {}
    })));
    _defineProperty(this, "run", async (key, fn, ok) => {
      this.setState({
        busy: key,
        err: null
      });
      try {
        const d = await fn();
        if (!this._alive) return null;
        if (ok) toast(typeof ok === "function" ? ok(d) : ok);
        this.setState({
          busy: null
        });
        return d;
      } catch (e) {
        if (this._alive) this.setState({
          busy: null,
          err: errMsg(e, "The action failed.")
        });
        return null;
      }
    });
    // ---- senders ---------------------------------------------------------------
    _defineProperty(this, "saveSender", async () => {
      const f = this.state.form;
      if (!f.fromName.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.fromEmail.trim())) {
        this.setState({
          err: "A from name and a valid from address are required."
        });
        return;
      }
      const body = {
        fromName: f.fromName.trim(),
        fromEmail: f.fromEmail.trim(),
        replyTo: f.replyTo.trim() || null,
        isDefault: !!f.isDefault
      };
      const d = await this.run("form", () => f.id ? api("PUT", `/senders/${f.id}`, body) : api("POST", "/senders", body), f.id ? "Sender updated." : "Sender added.");
      if (d) {
        this.setState({
          form: null
        });
        this.loadSenders();
      }
    });
    _defineProperty(this, "deleteSender", async s => {
      const d = await this.run(`del${s.id}`, () => api("DELETE", `/senders/${s.id}`), "Sender removed.");
      if (d) {
        this.setState({
          confirmDelete: null,
          dnsFor: this.state.dnsFor === s.id ? null : this.state.dnsFor
        });
        this.loadSenders();
      }
    });
    _defineProperty(this, "setDefaultSender", async s => {
      const d = await this.run(`def${s.id}`, () => api("POST", `/senders/${s.id}/set-default`, {}, {
        admin: true
      }), `${s.fromEmail} is now the default sender.`);
      if (d) this.loadSenders();
    });
    _defineProperty(this, "authenticate", async s => {
      const d = await this.run(`auth${s.id}`, () => api("POST", `/senders/${s.id}/authenticate-domain`, {}));
      if (d) {
        if (d.alreadyVerified) toast(`${s.domain} is already verified.`);
        this.setState({
          dnsFor: s.id,
          dns: d.dns || d.sender && d.sender.dns || []
        });
        this.loadSenders();
      }
    });
    _defineProperty(this, "verify", async s => {
      const d = await this.run(`ver${s.id}`, () => api("POST", `/senders/${s.id}/verify`, {}));
      if (d) {
        const camp = d.campaignDomain || {};
        toast(d.valid && camp.valid ? `${s.domain} verified for journeys, automations and campaigns.` : d.valid ? `${s.domain} verified for journeys and automations. Campaign records${camp.error ? `: ${camp.error}` : " are not valid yet"}${Array.isArray(d.reasons) && d.reasons.length ? ` (${d.reasons.join("; ")})` : ""}.` : `${s.domain}: DNS not valid yet${Array.isArray(d.reasons) && d.reasons.length ? ` (${d.reasons.join("; ")})` : ""}. Checked hourly too.`);
        this.setState({
          dnsFor: s.id,
          dns: d.dns || []
        });
        this.loadSenders();
      }
    });
    // ---- rails -----------------------------------------------------------------
    _defineProperty(this, "connectWithKey", async rail => {
      const key = this.state.apiKey.trim();
      if (!key) {
        this.setState({
          err: "Paste the API key first."
        });
        return;
      }
      const d = await this.run(`connect${rail}`, () => api("POST", `/rails/${rail}/connect`, {
        apiKey: key
      }, {
        admin: true
      }), `${railLabel(rail)} connected.`);
      if (d) {
        this.setState({
          apiKeyFor: null,
          apiKey: ""
        });
        this.loadRails();
      }
    });
    // Constant Contact (2026-10-03): every company brings its own v3 app — API key (client id) + client secret from
    // app.constantcontact.com → My Applications — then authorizes Plunk against that app by OAuth.
    _defineProperty(this, "connectConstantContact", async () => {
      const clientId = this.state.apiKey.trim(),
        clientSecret = (this.state.apiSecret || "").trim();
      if (!clientId || !clientSecret) {
        this.setState({
          err: "Enter the Constant Contact API key (client id) and the client secret."
        });
        return;
      }
      const d = await this.run("connectconstant_contact", () => api("POST", "/rails/constant_contact/connect", {
        clientId,
        clientSecret
      }, {
        admin: true
      }));
      if (!d) return;
      this.setState({
        apiKeyFor: null,
        apiKey: "",
        apiSecret: ""
      });
      await this.oauthStart("constant_contact");
    });
    _defineProperty(this, "oauthStart", async rail => {
      const returnTo = encodeURIComponent(`${window.location.origin}/Email/settings`);
      const d = await this.run(`oauth${rail}`, () => api("GET", `/rails/${rail}/oauth/start?redirect=false&returnTo=${returnTo}`));
      if (d && d.url) window.location.assign(d.url);
    });
    // Mailchimp shows the webhook signing key once at webhook creation; stored server-side (SSM), then every delivery is HMAC-verified.
    _defineProperty(this, "saveSigningKey", async rail => {
      const signingKey = (this.state.signingKey || "").trim();
      if (!signingKey) {
        this.setState({
          err: "Paste the signing key Mailchimp showed when the webhook was created."
        });
        return;
      }
      const d = await this.run(`sign${rail}`, () => api("POST", `/rails/${rail}/webhook-secret`, {
        signingKey
      }, {
        admin: true
      }), "Signing key saved. Mailchimp updates are now verified.");
      if (d) {
        this.setState({
          signingFor: null,
          signingKey: ""
        });
        this.loadRails();
      }
    });
    _defineProperty(this, "testRail", async rail => {
      this.setState({
        busy: `test${rail}`,
        err: null
      });
      try {
        const d = await api("POST", `/rails/${rail}/test`, {});
        if (!this._alive) return;
        this.setState({
          busy: null,
          railResult: {
            ...this.state.railResult,
            [rail]: {
              ok: d.ok !== false,
              text: `${d.status || "ok"}${d.accountName ? ` · ${d.accountName}` : ""}${d.detail ? ` · ${d.detail}` : ""} · ${dateTime(d.checkedDate || new Date())}`
            }
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          busy: null,
          railResult: {
            ...this.state.railResult,
            [rail]: {
              ok: false,
              text: errMsg(e, "Test failed.")
            }
          }
        });
      }
    });
    _defineProperty(this, "setDefaultRail", async rail => {
      const d = await this.run(`defr${rail}`, () => api("POST", `/rails/${rail}/set-default`, {}, {
        admin: true
      }), `${railLabel(rail)} is now the default sending service.`);
      if (d) this.loadRails();
    });
    _defineProperty(this, "disconnect", async rail => {
      const d = await this.run(`disc${rail}`, () => api("DELETE", `/rails/${rail}`, undefined, {
        admin: true
      }), `${railLabel(rail)} disconnected.`);
      if (d) {
        this.setState({
          confirmDisconnect: null
        });
        this.loadRails();
      }
    });
    _defineProperty(this, "copy", text => {
      try {
        navigator.clipboard.writeText(text).then(() => toast("Copied."));
      } catch (e) {
        toast("Copy failed: select the URL and copy it.");
      }
    });
    // ---- settings (saved as you change) ------------------------------------------
    _defineProperty(this, "settingsBody", s => {
      const body = {
        consentDefault: s.consentDefault || "legacy_allow",
        bulkCap: {
          max: Math.max(1, Number((s.bulkCap || {}).max) || 3),
          perHours: Math.max(1, Number((s.bulkCap || {}).perHours) || 168)
        },
        journeyCap: {
          max: Math.max(1, Number((s.journeyCap || {}).max) || 2),
          perHours: Math.max(1, Number((s.journeyCap || {}).perHours) || 24)
        },
        inactivityDays: Math.max(90, Number(s.inactivityDays) || 365),
        autoMerge: s.autoMerge || "email_exact",
        sendWithoutReview: !!s.sendWithoutReview,
        quietHours: s.quietHours || null,
        throttlePerHour: Math.max(100, Number(s.throttlePerHour) || 50000)
      };
      if (s.guard) body.guard = s.guard;
      return body;
    });
    _defineProperty(this, "setDraft", patch => {
      if (!isAdmin()) return;
      this.setState({
        settingsDraft: {
          ...this.state.settingsDraft,
          ...patch
        },
        saveState: "pending",
        saveErr: null
      });
      clearTimeout(this._saveT);
      this._saveT = setTimeout(this.autoSave, SAVE_DELAY);
    });
    _defineProperty(this, "autoSave", async () => {
      this._saveT = null;
      const seq = ++this._saveSeq;
      this.setState({
        saveState: "saving",
        saveErr: null
      });
      try {
        const d = await api("PUT", "/settings", this.settingsBody(this.state.settingsDraft), {
          admin: true
        });
        if (!this._alive || seq !== this._saveSeq) return;
        // The draft stays as typed (a newer edit may already be waiting); the saved copy feeds "last changed".
        this.setState({
          settings: d || this.state.settings,
          saveState: this._saveT ? "pending" : "saved"
        });
      } catch (e) {
        if (!this._alive || seq !== this._saveSeq) return;
        this.setState({
          saveState: "error",
          saveErr: errMsg(e, "Your change could not be saved. Try again.")
        });
      }
    });
    _defineProperty(this, "retrySave", () => {
      clearTimeout(this._saveT);
      this.autoSave();
    });
    _defineProperty(this, "toggleDetails", name => this.setState({
      details: {
        ...this.state.details,
        [name]: !this.state.details[name]
      }
    }));
    this.state = {
      senders: null,
      rails: null,
      settings: null,
      form: null,
      dnsFor: null,
      dns: null,
      busy: null,
      err: null,
      notice: null,
      apiKeyFor: null,
      apiKey: "",
      railResult: {},
      settingsDraft: null,
      saveState: null,
      saveErr: null,
      showLimits: false,
      showSpeed: false,
      details: {},
      confirmDelete: null,
      confirmDisconnect: null
    };
    this._alive = true;
    this._saveSeq = 0;
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    // Back from the provider's OAuth consent: the API redirects to this page with ?connected= or ?error=.
    try {
      const p = new URLSearchParams(this.props.location.search || "");
      if (p.get("connected")) this.setState({
        notice: {
          tone: "success",
          text: `${railLabel(p.get("connected"))} connected.`
        }
      });else if (p.get("error")) this.setState({
        notice: {
          tone: "danger",
          text: `${railLabel(p.get("rail") || "")} connect failed: ${p.get("error")}`
        }
      });
      if (p.get("connected") || p.get("error")) this.props.history.replace(this.props.location.pathname);
    } catch (e) {/* no query */}
    this.loadAll();
  }
  componentWillUnmount() {
    this._alive = false;
    // A change still waiting on the debounce is saved on the way out rather than dropped.
    if (this._saveT) {
      clearTimeout(this._saveT);
      this._saveT = null;
      if (this.state.settingsDraft) api("PUT", "/settings", this.settingsBody(this.state.settingsDraft), {
        admin: true
      }).catch(() => null);
    }
  }
  // ---- render ----------------------------------------------------------------
  renderSenderForm() {
    const f = this.state.form;
    if (!f) return null;
    const set = k => e => this.setState({
      form: {
        ...f,
        [k]: e.target.value
      }
    });
    const existing = f.id ? (this.state.senders || []).find(x => x.id === f.id) : null;
    const records = existing ? (this.state.dnsFor === existing.id && this.state.dns && this.state.dns.length ? this.state.dns : existing.dns) || [] : [];
    const status = existing ? existing.domainAuthStatus : null;
    const busy = this.state.busy;
    return /*#__PURE__*/React.createElement(Dialog, {
      title: f.id ? "Edit sender" : "Add a sender",
      onClose: () => this.setState({
        form: null,
        err: null
      }),
      width: 560
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-form"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "From name"), /*#__PURE__*/React.createElement("input", {
      autoFocus: true,
      value: f.fromName,
      placeholder: "Harbor & Oak",
      onChange: set("fromName")
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "From address"), /*#__PURE__*/React.createElement("input", {
      value: f.fromEmail,
      placeholder: "hello@yourdomain.com",
      onChange: set("fromEmail"),
      disabled: !!f.id && f.locked
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Reply-to (optional)"), /*#__PURE__*/React.createElement("input", {
      value: f.replyTo,
      placeholder: "care@yourdomain.com",
      onChange: set("replyTo")
    }), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Replies go here instead of the from address.")), /*#__PURE__*/React.createElement("label", {
      className: "ec-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!f.isDefault,
      onChange: e => this.setState({
        form: {
          ...f,
          isDefault: e.target.checked
        }
      })
    }), /*#__PURE__*/React.createElement("span", null, "Use as the default sender for new campaigns")), existing ? /*#__PURE__*/React.createElement("div", {
      className: "ec-domain"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-domain-head"
    }, /*#__PURE__*/React.createElement("span", null, "Domain ", /*#__PURE__*/React.createElement("b", null, existing.domain || "-")), /*#__PURE__*/React.createElement(Pill, {
      tone: domainTone(status)
    }, domainLabel(status)), /*#__PURE__*/React.createElement("span", {
      className: "ec-domain-actions"
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: busy === `auth${existing.id}`,
      onClick: () => this.authenticate(existing)
    }, busy === `auth${existing.id}` ? "…" : status === "verified" ? records.length ? "Refresh DNS records" : "Show DNS records" : records.length ? "Refresh records" : "Get DNS records"), status === "pending" || status !== "verified" && records.length || status === "verified" && !(existing.campaignDomain && existing.campaignDomain.valid) ? /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: busy === `ver${existing.id}`,
      onClick: () => this.verify(existing)
    }, busy === `ver${existing.id}` ? "…" : "Verify now") : null)), status === "verified" ? existing.campaignDomain && existing.campaignDomain.valid ? /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Verified", existing.verifiedDate ? ` on ${dateTime(existing.verifiedDate)}` : "", ". Journeys, automations and campaigns send from this address.", records.length ? " Keep these records in place at your DNS host." : "") : /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Verified for journeys and automations", existing.verifiedDate ? ` on ${dateTime(existing.verifiedDate)}` : "", ". Campaigns use a separate sending account that needs its own records: click \"", records.length ? "Refresh DNS records" : "Show DNS records", "\", add any new records at your DNS host, then \"Verify now\". Until then campaigns go out from no-reply@plunk.io with replies to you.", existing.campaignDomain && existing.campaignDomain.error ? ` (${existing.campaignDomain.error})` : "") : !records.length ? /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Before email can go out from this address, Plunk needs to verify you own the domain. Click \"Get DNS records\", add them at your DNS host, then \"Verify now\".") : /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Add these records at your DNS host (where ", existing.domain, " is managed), then click \"Verify now\". We also re-check every hour."), records.length ? /*#__PURE__*/React.createElement("div", {
      className: "ec-dns"
    }, records.map((r, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: r.valid ? "ok" : ""
    }, /*#__PURE__*/React.createElement("span", {
      className: "type"
    }, r.type || "CNAME"), /*#__PURE__*/React.createElement("span", {
      className: "host"
    }, r.host), /*#__PURE__*/React.createElement("span", {
      className: "data"
    }, r.data), /*#__PURE__*/React.createElement("span", {
      className: "state"
    }, r.valid ? "found" : "not found yet"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.copy(`${r.host}\t${r.type || "CNAME"}\t${r.data}`)
    }, "Copy")))) : null) : /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "After saving, open the sender again to get the DNS records that verify your domain."), this.state.err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err",
      style: {
        marginTop: 8
      }
    }, this.state.err) : null), /*#__PURE__*/React.createElement(DialogActionsBar, null, /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        form: null,
        err: null
      })
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: busy === "form",
      onClick: this.saveSender
    }, busy === "form" ? "Saving…" : "Save")));
  }
  renderSenders() {
    const {
      senders,
      busy,
      confirmDelete
    } = this.state;
    const admin = isAdmin();
    const openForm = s => this.setState({
      form: {
        id: s.id,
        fromName: s.fromName || "",
        fromEmail: s.fromEmail || "",
        replyTo: s.replyTo || "",
        isDefault: !!s.isDefault
      },
      err: null,
      dnsFor: s.id,
      dns: s.dns || []
    });
    return /*#__PURE__*/React.createElement("section", {
      className: "ems-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-title"
    }, /*#__PURE__*/React.createElement("b", null, "Who it's from"), /*#__PURE__*/React.createElement("span", null, "The name and address people see in their inbox")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn",
      onClick: () => this.setState({
        form: {
          fromName: "",
          fromEmail: "",
          replyTo: "",
          isDefault: !senders.length
        },
        err: null
      })
    }, "Add a sender")), !senders.length ? /*#__PURE__*/React.createElement("div", {
      className: "ems-empty"
    }, "No senders yet. Add one, then verify its domain so email can go out.") : senders.map(s => {
      const [stText, stTone] = senderStatus(s);
      const verified = String(s.domainAuthStatus || "").toLowerCase() === "verified";
      return /*#__PURE__*/React.createElement("div", {
        key: s.id,
        className: "ems-row"
      }, /*#__PURE__*/React.createElement("span", {
        className: "ems-avatar",
        "aria-hidden": "true"
      }, String(s.fromName || s.fromEmail || "?").trim().charAt(0).toUpperCase()), /*#__PURE__*/React.createElement("div", {
        className: "ems-row-main"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ems-row-top"
      }, /*#__PURE__*/React.createElement("b", null, s.fromName), /*#__PURE__*/React.createElement("span", {
        className: "ems-addr"
      }, s.fromEmail), s.isDefault ? /*#__PURE__*/React.createElement("span", {
        className: "ems-tag"
      }, "Default") : null), /*#__PURE__*/React.createElement("span", {
        className: "ems-sub"
      }, "Replies go to ", s.replyTo || s.fromEmail), confirmDelete === s.id ? /*#__PURE__*/React.createElement("span", {
        className: "ems-confirm"
      }, "Remove this sender?", /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ems-link danger",
        disabled: busy === `del${s.id}`,
        onClick: () => this.deleteSender(s)
      }, busy === `del${s.id}` ? "Removing…" : "Yes, remove"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ems-link",
        onClick: () => this.setState({
          confirmDelete: null
        })
      }, "Keep")) : null), stTone === "ok" ? /*#__PURE__*/React.createElement("span", {
        className: "ems-state ok"
      }, stText) : /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: `ems-state ${stTone} as-link`,
        title: "Open to see the DNS records",
        onClick: () => openForm(s)
      }, stText), /*#__PURE__*/React.createElement(RowMenu, {
        label: `More for ${s.fromEmail}`,
        items: [{
          label: verified ? "Edit" : "Edit and verify",
          onClick: () => openForm(s)
        }, admin && !s.isDefault ? {
          label: "Make default",
          disabled: busy === `def${s.id}`,
          onClick: () => this.setDefaultSender(s)
        } : null, admin ? {
          label: "Remove",
          danger: true,
          onClick: () => this.setState({
            confirmDelete: s.id
          })
        } : null]
      }));
    }));
  }
  renderRailDetails(name, r, connected) {
    const {
      busy,
      signingFor
    } = this.state;
    const admin = isAdmin();
    const cfg = r.config || {};
    return /*#__PURE__*/React.createElement("div", {
      className: "ems-details"
    }, name === "mailchimp" && r.webhookUrl ? /*#__PURE__*/React.createElement("div", {
      className: "ems-step"
    }, /*#__PURE__*/React.createElement("div", null, "In Mailchimp, open ", /*#__PURE__*/React.createElement("b", null, "Audience"), ", then ", /*#__PURE__*/React.createElement("b", null, "Settings"), ", then ", /*#__PURE__*/React.createElement("b", null, "Webhooks"), ", and add this address. Unsubscribes and results then come back to Plunk."), /*#__PURE__*/React.createElement("div", {
      className: "ems-copy"
    }, /*#__PURE__*/React.createElement("code", {
      title: r.webhookUrl
    }, r.webhookUrl.replace(/^https?:\/\//, "").split("?")[0], "?\u2026"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm",
      onClick: () => this.copy(r.webhookUrl)
    }, "Copy address"))) : null, name === "mailchimp" && connected ? /*#__PURE__*/React.createElement("div", {
      className: "ems-step"
    }, signingFor === name ? /*#__PURE__*/React.createElement("span", {
      className: "ems-inline"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      style: {
        width: 240
      },
      type: "password",
      autoFocus: true,
      value: this.state.signingKey || "",
      placeholder: "Signing key from Mailchimp",
      onChange: e => this.setState({
        signingKey: e.target.value
      })
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm",
      disabled: busy === `sign${name}`,
      onClick: () => this.saveSigningKey(name)
    }, busy === `sign${name}` ? "Saving…" : "Save"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.setState({
        signingFor: null,
        signingKey: ""
      })
    }, "Cancel")) : /*#__PURE__*/React.createElement(React.Fragment, null, cfg.webhookSigned ? "Updates from Mailchimp are checked with your signing key." : "Then paste the signing key Mailchimp shows when you add the address, so Plunk can check each update is really from Mailchimp.", admin ? /*#__PURE__*/React.createElement(React.Fragment, null, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.setState({
        signingFor: name,
        signingKey: ""
      })
    }, cfg.webhookSigned ? "Replace key" : "Add signing key")) : null)) : null, connected && (cfg.listName || cfg.listId) ? /*#__PURE__*/React.createElement("div", {
      className: "ems-fine"
    }, "Mailchimp audience: ", cfg.listName || cfg.listId, cfg.listName && cfg.listId ? ` (${cfg.listId})` : "", cfg.memberCount ? ` · ${num(cfg.memberCount)} contacts` : "") : null, connected && (r.statusDetail || r.lastSync) ? /*#__PURE__*/React.createElement("div", {
      className: "ems-fine"
    }, r.statusDetail || "Connected", r.lastSync ? ` · last checked ${dateTime(r.lastSync)}` : "") : null);
  }
  renderConnectForm(name, r) {
    const {
      busy,
      apiKeyFor
    } = this.state;
    const admin = isAdmin();
    if (apiKeyFor !== name) return null;
    if (name === "constant_contact") return /*#__PURE__*/React.createElement("div", {
      className: "ems-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ems-inline wrap"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      style: {
        flex: "1 1 180px"
      },
      autoFocus: true,
      value: this.state.apiKey,
      placeholder: "API key (client id)",
      onChange: e => this.setState({
        apiKey: e.target.value
      })
    }), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      style: {
        flex: "1 1 180px"
      },
      type: "password",
      value: this.state.apiSecret || "",
      placeholder: "Client secret",
      onChange: e => this.setState({
        apiSecret: e.target.value
      })
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm",
      disabled: busy === `connect${name}` || busy === `oauth${name}` || !admin,
      onClick: this.connectConstantContact
    }, busy === `connect${name}` || busy === `oauth${name}` ? "Connecting…" : "Save and connect"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.setState({
        apiKeyFor: null,
        apiKey: "",
        apiSecret: ""
      })
    }, "Cancel")), /*#__PURE__*/React.createElement("div", {
      className: "ems-fine"
    }, "Find these in your Constant Contact developer account, under My Applications. Add Plunk's callback address to that app as a redirect address."));
    return /*#__PURE__*/React.createElement("div", {
      className: "ems-details"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ems-inline wrap"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      style: {
        flex: "1 1 200px"
      },
      type: "password",
      autoFocus: true,
      value: this.state.apiKey,
      placeholder: `${railLabel(name)} API key`,
      onChange: e => this.setState({
        apiKey: e.target.value
      })
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm",
      disabled: busy === `connect${name}` || !admin,
      onClick: () => this.connectWithKey(name)
    }, busy === `connect${name}` ? "Connecting…" : "Connect"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.setState({
        apiKeyFor: null,
        apiKey: ""
      })
    }, "Cancel"), r.oauth ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.oauthStart(name)
    }, "Sign in to Mailchimp instead") : null), /*#__PURE__*/React.createElement("div", {
      className: "ems-fine"
    }, "In Mailchimp: Account, then Extras, then API keys."));
  }
  renderRail(name, r, rails) {
    const {
      busy,
      railResult,
      confirmDisconnect,
      details
    } = this.state;
    const admin = isAdmin();
    const staff = account.inGroup("root");
    const cfg = r.config || {};
    const connected = name === "plunk" || r.connected || r.status === "connected";
    const trouble = !connected && (r.status === "error" || r.status === "expired" || r.status === "revoked");
    const isDefault = (rails.default || "plunk") === name;
    const res = railResult[name];
    const open = !!details[name];
    const setupLeft = name === "mailchimp" && connected && !cfg.webhookSigned;
    const ccKeys = name === "constant_contact" && (cfg.ccApp || r.oauth);
    let sub;
    if (name === "plunk" || !connected) sub = /*#__PURE__*/React.createElement("span", {
      className: "ems-sub"
    }, trouble && r.statusDetail ? r.statusDetail : RAIL_TEXT[name]);else if (setupLeft) sub = /*#__PURE__*/React.createElement("span", {
      className: "ems-sub warn"
    }, "One step left: add the Plunk address in Mailchimp so unsubscribes come back. ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      "aria-expanded": open,
      onClick: () => this.toggleDetails(name)
    }, open ? "Hide" : "Show me how"));else sub = /*#__PURE__*/React.createElement("span", {
      className: "ems-sub"
    }, RAIL_TEXT[name], ". ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      "aria-expanded": open,
      onClick: () => this.toggleDetails(name)
    }, open ? "Hide details" : "Details"));
    return /*#__PURE__*/React.createElement("div", {
      key: name,
      className: "ems-rail" + (isDefault ? " is-default" : "")
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-row bare"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-row-main"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-row-top"
    }, /*#__PURE__*/React.createElement("b", null, railLabel(name)), isDefault ? /*#__PURE__*/React.createElement("span", {
      className: "ems-tag"
    }, "Default") : null, name !== "plunk" && connected ? /*#__PURE__*/React.createElement("span", {
      className: "ems-state ok"
    }, "\u25CF Connected") : null, trouble ? /*#__PURE__*/React.createElement("span", {
      className: "ems-state bad"
    }, "Needs attention") : null), sub, name === "plunk" && staff && r.sendsAs ? /*#__PURE__*/React.createElement("span", {
      className: "ems-staff" + (r.sendsAs.fallback ? " bad" : "")
    }, "Plunk staff only: campaigns send through SendGrid subuser ", /*#__PURE__*/React.createElement("b", null, r.sendsAs.subuser || "?"), r.sendsAs.from ? ` (${r.sendsAs.from})` : "", r.sendsAs.note ? ` · ${r.sendsAs.note}` : "", "; journeys and automations always use plunk-apps.") : null), !isDefault && connected && admin ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm quiet",
      disabled: busy === `defr${name}`,
      onClick: () => this.setDefaultRail(name)
    }, busy === `defr${name}` ? "…" : "Make default") : null, !connected && name === "mailchimp" ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm quiet",
      disabled: !admin,
      title: admin ? "" : "Only an admin can connect a service",
      onClick: () => this.setState({
        apiKeyFor: name,
        apiKey: ""
      })
    }, trouble ? "Reconnect" : "Connect") : null, !connected && name === "constant_contact" ? ccKeys ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm quiet",
      disabled: busy === `oauth${name}`,
      onClick: () => this.oauthStart(name)
    }, busy === `oauth${name}` ? "…" : trouble ? "Reconnect" : "Connect") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-btn sm quiet",
      disabled: !admin,
      title: admin ? "" : "Only an admin can connect a service",
      onClick: () => this.setState({
        apiKeyFor: name,
        apiKey: "",
        apiSecret: ""
      })
    }, "Connect") : null, name !== "plunk" ? /*#__PURE__*/React.createElement(RowMenu, {
      label: `More for ${railLabel(name)}`,
      items: [connected ? {
        label: busy === `test${name}` ? "Testing…" : "Test connection",
        disabled: busy === `test${name}`,
        onClick: () => this.testRail(name)
      } : null, connected && name === "mailchimp" && admin ? {
        label: "Replace key",
        onClick: () => this.setState({
          details: {
            ...details,
            [name]: true
          },
          signingFor: name,
          signingKey: ""
        })
      } : null, !connected && ccKeys && admin ? {
        label: "Change app keys",
        onClick: () => this.setState({
          apiKeyFor: name,
          apiKey: "",
          apiSecret: ""
        })
      } : null, connected && admin ? {
        label: "Disconnect",
        danger: true,
        onClick: () => this.setState({
          confirmDisconnect: name
        })
      } : null]
    }) : null), confirmDisconnect === name ? /*#__PURE__*/React.createElement("span", {
      className: "ems-confirm"
    }, "Disconnect ", railLabel(name), "? Campaigns set to use it go out through Plunk instead.", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link danger",
      disabled: busy === `disc${name}`,
      onClick: () => this.disconnect(name)
    }, busy === `disc${name}` ? "Disconnecting…" : "Yes, disconnect"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.setState({
        confirmDisconnect: null
      })
    }, "Keep")) : null, res ? /*#__PURE__*/React.createElement("span", {
      className: "ems-fine " + (res.ok ? "ok" : "bad")
    }, res.ok ? "Connection works" : "Connection failed", " \xB7 ", res.text) : null, connected && name !== "plunk" && open ? this.renderRailDetails(name, r, connected) : null, !connected ? this.renderConnectForm(name, r) : null);
  }
  renderRules() {
    const {
      settings,
      settingsDraft,
      saveState,
      saveErr,
      showLimits,
      showSpeed
    } = this.state;
    const admin = isAdmin();
    const sd = settingsDraft || {};
    const cap = k => sd[k] || settings[k] || {};
    const guard = sd.guard || settings.guard || {};
    const qh = sd.quietHours || settings.quietHours || null;
    const complaint = guard.complaintRate == null ? 0.1 : guard.complaintRate;
    const bounce = guard.hardBounceRate == null ? 2 : guard.hardBounceRate;
    const warnAt = guard.warnAtPct == null ? 80 : guard.warnAtPct;
    const speed = sd.throttlePerHour || settings.throttlePerHour || 50000;
    const status = !admin ? /*#__PURE__*/React.createElement("span", {
      className: "ems-save dim"
    }, "Only an admin can change these") : saveState === "saving" || saveState === "pending" ? /*#__PURE__*/React.createElement("span", {
      className: "ems-save dim"
    }, "Saving\u2026") : saveState === "saved" ? /*#__PURE__*/React.createElement("span", {
      className: "ems-save ok"
    }, "Saved") : saveState === "error" ? /*#__PURE__*/React.createElement("span", {
      className: "ems-save bad"
    }, "Not saved") : null;
    return /*#__PURE__*/React.createElement("section", {
      className: "ems-card ems-rules"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-title"
    }, /*#__PURE__*/React.createElement("b", null, "Sending rules"), /*#__PURE__*/React.createElement("span", null, "Apply to every campaign and journey")), status), saveState === "error" ? /*#__PURE__*/React.createElement("div", {
      className: "ems-error"
    }, saveErr, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: this.retrySave
    }, "Try again")) : null, /*#__PURE__*/React.createElement("div", {
      className: "ems-group"
    }, /*#__PURE__*/React.createElement("label", {
      className: "ems-group-title",
      htmlFor: "ems-consent"
    }, "Who can get marketing email"), /*#__PURE__*/React.createElement("select", {
      id: "ems-consent",
      className: "ec-input ems-select",
      disabled: !admin,
      value: sd.consentDefault || settings.consentDefault || "legacy_allow",
      onChange: e => this.setDraft({
        consentDefault: e.target.value
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: "legacy_allow"
    }, "Everyone who hasn't unsubscribed"), /*#__PURE__*/React.createElement("option", {
      value: "opt_in_only"
    }, "Only people who said yes to email"))), /*#__PURE__*/React.createElement("div", {
      className: "ems-group"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ems-group-title"
    }, "Don't overdo it"), /*#__PURE__*/React.createElement("span", {
      className: "ems-sentence"
    }, "No more than ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num",
      type: "number",
      min: "1",
      "aria-label": "Campaigns per person",
      disabled: !admin,
      value: cap("bulkCap").max || 3,
      onChange: e => this.setDraft({
        bulkCap: {
          ...cap("bulkCap"),
          max: Number(e.target.value) || 1
        }
      })
    }), " campaigns per person every ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num",
      type: "number",
      min: "1",
      "aria-label": "Days",
      disabled: !admin,
      value: Math.round((cap("bulkCap").perHours || 168) / 24),
      onChange: e => this.setDraft({
        bulkCap: {
          ...cap("bulkCap"),
          perHours: (Number(e.target.value) || 7) * 24
        }
      })
    }), " days"), /*#__PURE__*/React.createElement("span", {
      className: "ems-sentence"
    }, "No more than ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num",
      type: "number",
      min: "1",
      "aria-label": "Journey emails per day",
      disabled: !admin,
      value: cap("journeyCap").max || 2,
      onChange: e => this.setDraft({
        journeyCap: {
          max: Number(e.target.value) || 1,
          perHours: 24
        }
      })
    }), " journey emails per person a day"), /*#__PURE__*/React.createElement("label", {
      className: "ems-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: !admin,
      checked: !!(qh && qh.enabled),
      onChange: e => this.setDraft({
        quietHours: {
          ...(qh || {
            startHour: 21,
            endHour: 8
          }),
          enabled: e.target.checked
        }
      })
    }), "Hold journey emails overnight"), qh && qh.enabled ? /*#__PURE__*/React.createElement("span", {
      className: "ems-sentence indent"
    }, "From ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num",
      type: "number",
      min: "0",
      max: "23",
      "aria-label": "Start hour",
      disabled: !admin,
      value: qh.startHour == null ? 21 : qh.startHour,
      onChange: e => this.setDraft({
        quietHours: {
          ...qh,
          startHour: Number(e.target.value)
        }
      })
    }), ":00 to ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num",
      type: "number",
      min: "0",
      max: "23",
      "aria-label": "End hour",
      disabled: !admin,
      value: qh.endHour == null ? 8 : qh.endHour,
      onChange: e => this.setDraft({
        quietHours: {
          ...qh,
          endHour: Number(e.target.value)
        }
      })
    }), ":00 in ", /*#__PURE__*/React.createElement("input", {
      className: "ec-input ems-tz",
      disabled: !admin,
      value: qh.timezone || "",
      placeholder: "Time zone, like America/New_York",
      "aria-label": "Time zone",
      onChange: e => this.setDraft({
        quietHours: {
          ...qh,
          timezone: e.target.value
        }
      })
    })) : null, /*#__PURE__*/React.createElement("span", {
      className: "ems-fine"
    }, "Each journey can change these in its own settings.")), /*#__PURE__*/React.createElement("div", {
      className: "ems-group"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ems-group-title"
    }, "Protect your sender reputation"), /*#__PURE__*/React.createElement("label", {
      className: "ems-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: !admin,
      checked: guard.autoPause !== false,
      onChange: e => this.setDraft({
        guard: {
          ...guard,
          autoPause: e.target.checked
        }
      })
    }), "Pause a campaign if too many people mark it as spam or it bounces a lot"), /*#__PURE__*/React.createElement("span", {
      className: "ems-fine indent"
    }, guard.autoPause !== false ? "Pauses at " : "Limits: ", spamLimit(complaint), ", or ", num(bounce), "% bounced. You're told at ", num(warnAt), "%.", guard.autoPause === false ? " A campaign keeps sending past a limit until you pause it." : "", admin ? /*#__PURE__*/React.createElement(React.Fragment, null, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      "aria-expanded": showLimits,
      onClick: () => this.setState({
        showLimits: !showLimits
      })
    }, showLimits ? "Done" : "Change the limits")) : null), showLimits && admin ? /*#__PURE__*/React.createElement("div", {
      className: "ems-limits indent"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ems-sentence"
    }, "Spam reports: ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num wide",
      type: "number",
      min: "0.01",
      max: "100",
      step: "0.01",
      "aria-label": "Spam report limit, percent of delivered",
      value: complaint,
      onChange: e => this.setDraft({
        guard: {
          ...guard,
          complaintRate: Number(e.target.value) || 0.1
        }
      })
    }), "% of delivered"), /*#__PURE__*/React.createElement("span", {
      className: "ems-sentence"
    }, "Bounces: ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num wide",
      type: "number",
      min: "0.1",
      max: "100",
      step: "0.1",
      "aria-label": "Bounce limit, percent of sent",
      value: bounce,
      onChange: e => this.setDraft({
        guard: {
          ...guard,
          hardBounceRate: Number(e.target.value) || 2
        }
      })
    }), "% of sent"), /*#__PURE__*/React.createElement("span", {
      className: "ems-sentence"
    }, "Tell me at ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num",
      type: "number",
      min: "10",
      max: "99",
      "aria-label": "Warning point, percent of either limit",
      value: warnAt,
      onChange: e => this.setDraft({
        guard: {
          ...guard,
          warnAtPct: Number(e.target.value) || 80
        }
      })
    }), "% of either limit"), /*#__PURE__*/React.createElement("span", {
      className: "ems-fine"
    }, "You get a message in Plunk and by email, and the campaign page shows a banner.")) : null), /*#__PURE__*/React.createElement("div", {
      className: "ems-group"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ems-group-title"
    }, "Approval"), /*#__PURE__*/React.createElement("label", {
      className: "ems-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: !admin,
      checked: sd.sendWithoutReview != null ? !!sd.sendWithoutReview : !!settings.sendWithoutReview,
      onChange: e => this.setDraft({
        sendWithoutReview: e.target.checked
      })
    }), "Admins can send without someone else approving")), /*#__PURE__*/React.createElement("div", {
      className: "ems-foot"
    }, showSpeed && admin ? /*#__PURE__*/React.createElement("span", {
      className: "ems-sentence"
    }, "Sending speed: up to ", /*#__PURE__*/React.createElement("input", {
      className: "ems-num wide",
      type: "number",
      min: "100",
      step: "100",
      "aria-label": "Emails per hour",
      value: speed,
      onChange: e => this.setDraft({
        throttlePerHour: Number(e.target.value) || 50000
      })
    }), " an hour ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.setState({
        showSpeed: false
      })
    }, "Done")) : /*#__PURE__*/React.createElement("span", null, "Sending speed: up to ", num(speed), " an hour", admin ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ems-link",
      onClick: () => this.setState({
        showSpeed: true
      })
    }, "Change")) : null), settings.modifiedDate ? /*#__PURE__*/React.createElement("span", null, "Last changed ", dateTime(settings.modifiedDate), settings.modifiedBy ? ` by ${settings.modifiedBy}` : "") : null));
  }
  render() {
    if (!hasTenantFeature("emailAutomation")) return /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Email isn't set up for this workspace."));
    const {
      senders,
      rails,
      settings,
      err,
      notice
    } = this.state;
    if (!senders || !rails || !settings) return /*#__PURE__*/React.createElement(Loader, null);
    const byRail = {};
    (rails.items || []).forEach(r => {
      byRail[String(r.rail).toLowerCase()] = r;
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view ec-view ems-view"
    }, /*#__PURE__*/React.createElement(EmailHeader, null), notice ? /*#__PURE__*/React.createElement("div", {
      className: `wa-notice ${notice.tone === "danger" ? "danger" : ""}`
    }, notice.text) : null, err && !this.state.form ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, err) : null, this.renderSenderForm(), /*#__PURE__*/React.createElement("div", {
      className: "ems-grid"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-col"
    }, this.renderSenders(), /*#__PURE__*/React.createElement("section", {
      className: "ems-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-head"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ems-title"
    }, /*#__PURE__*/React.createElement("b", null, "How it's sent"), /*#__PURE__*/React.createElement("span", null, "Campaigns go out through ", railLabel(rails.default || "plunk"), " unless you pick another service"))), RAIL_ORDER.map(name => this.renderRail(name, byRail[name] || {
      rail: name,
      status: "disconnected"
    }, rails))), hasTenantFeature("outreach") ? /*#__PURE__*/React.createElement(OutreachSettingsCard, null) : null), this.renderRules()));
  }
}
export default withRouter(EmailSendersViewComponent);